import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, Circle, ChevronRight, Printer } from "lucide-react";

type PracticeLevel = "specialist" | "advanced" | "consultant";
const LEVEL_KEY = "rheumhub_practice_level";

interface Statement {
  id: number;
  code: string;
  statement: string;
  specialistLevel: string;
  advancedLevel: string;
  consultantLevel: string;
  portfolioEntry: { status: string } | null;
}

interface Domain {
  id: number;
  code: string;
  title: string;
}

export default function CompetencyPlan() {
  const [level] = useState<PracticeLevel>(
    () => (localStorage.getItem(LEVEL_KEY) as PracticeLevel) ?? "specialist"
  );
  const [domains, setDomains] = useState<Domain[]>([]);
  const [statementsByDomain, setStatementsByDomain] = useState<Map<number, Statement[]>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const domainsRes = await fetch("/api/competency/domains");
        if (!domainsRes.ok) return;
        const domainsData: Domain[] = await domainsRes.json();
        setDomains(domainsData);

        const stmtMap = new Map<number, Statement[]>();
        await Promise.all(
          domainsData.map(async (d) => {
            const res = await fetch(`/api/competency/domains/${d.id}/statements`);
            if (res.ok) {
              const stmts: Statement[] = await res.json();
              stmtMap.set(
                d.id,
                stmts.filter((s) => {
                  const st = s.portfolioEntry?.status ?? "not_started";
                  return st === "not_started" || st === "in_progress";
                })
              );
            }
          })
        );
        setStatementsByDomain(stmtMap);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const levelText = (s: Statement) => {
    if (level === "specialist") return s.specialistLevel;
    if (level === "advanced") return s.advancedLevel;
    return s.consultantLevel;
  };

  const totalPending = Array.from(statementsByDomain.values()).reduce((a, b) => a + b.length, 0);

  return (
    <NurseLayout title="Development Plan">
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <Link href="/nurse/competency">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Portfolio Overview
            </Button>
          </Link>
          <Button
            onClick={() => window.print()}
            variant="outline"
            size="sm"
            className="gap-2 print:hidden"
          >
            <Printer className="h-4 w-4" />
            Print Plan
          </Button>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm print:border-0 print:shadow-none">
          <h2 className="text-xl font-bold text-slate-900 mb-1">Competency Development Plan</h2>
          <p className="text-sm text-slate-500 mb-4">
            {totalPending} competencies to complete at <strong className="capitalize">{level} Practitioner</strong> level · Generated {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </p>

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-xl" />)}
            </div>
          ) : totalPending === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">All competencies are marked as achieved. Well done!</p>
            </div>
          ) : (
            <div className="space-y-8">
              {domains.map((d) => {
                const stmts = statementsByDomain.get(d.id) ?? [];
                if (stmts.length === 0) return null;
                return (
                  <div key={d.id}>
                    <h3 className="font-bold text-slate-800 text-sm mb-3 pb-2 border-b border-slate-100">
                      <span className="text-slate-400 font-mono mr-2">{d.code}</span>
                      {d.title}
                    </h3>
                    <div className="space-y-2">
                      {stmts.map((s) => {
                        const status = s.portfolioEntry?.status ?? "not_started";
                        const isInProgress = status === "in_progress";
                        return (
                          <div key={s.id} className="flex gap-3 items-start p-3 bg-slate-50 rounded-xl">
                            <div className="shrink-0 mt-0.5">
                              {isInProgress
                                ? <Clock className="h-4 w-4 text-amber-500" />
                                : <Circle className="h-4 w-4 text-slate-300" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-xs font-mono text-slate-400">{s.code}</span>
                                {isInProgress && <span className="text-xs text-amber-600 font-medium">In Progress</span>}
                              </div>
                              <p className="text-sm font-medium text-slate-800 mb-1">{s.statement}</p>
                              <p className="text-xs text-slate-500 leading-relaxed">{levelText(s)}</p>
                            </div>
                            <Link href={`/nurse/competency/${d.id}/${s.id}`} className="print:hidden">
                              <button className="shrink-0 p-1 hover:bg-slate-200 rounded-lg transition-colors">
                                <ChevronRight className="h-4 w-4 text-slate-400" />
                              </button>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 print:hidden">
          This development plan shows competencies not yet marked as achieved. Use it to guide your next professional development conversation with your line manager or mentor.
        </div>
      </div>

      <style>{`
        @media print {
          .no-print, nav, aside, header, footer, button { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </NurseLayout>
  );
}
